<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  CARD_SEO_DATA,
  MINOR_ARCANA_SLUGS,
  SUIT_LABELS,
  SUIT_ORDER,
  SUIT_PAPER,
  cardImageSrc,
  type CardSuit,
} from '@app/data/cardSeoData';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import CardFan from '@app/presentation/components/paper/CardFan.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

const MASTI_TITLE = 'Масти Таро — Кубки, Пентакли, Мечи и Жезлы | Таро Мяу';
const MASTI_DESCRIPTION =
  'Четыре масти младших арканов Таро: Кубки (вода, чувства), Пентакли (земля, деньги и тело), Мечи (воздух, мысли) и Жезлы (огонь, действие). Что значит каждая масть и за что отвечает.';

// Per-suit chrome: element, one-line theme; the patch paper is shared with the suit pages.
const SUIT_META: Record<CardSuit, { element: string; theme: string }> = {
  kubki: { element: 'Вода', theme: 'Чувства, любовь, отношения и внутренний мир.' },
  pentakli: { element: 'Земля', theme: 'Деньги, тело, работа и всё материальное.' },
  mechi: { element: 'Воздух', theme: 'Мысли, слова, правда и конфликты.' },
  zhezly: { element: 'Огонь', theme: 'Действие, страсть, энергия и воля.' },
};

// The suit's Ace, Queen and King, face up, for the corner fan (same-origin webp).
const suitFaces = (suit: CardSuit): string[] =>
  MINOR_ARCANA_SLUGS.filter((s) => CARD_SEO_DATA[s]?.suit === suit)
    .filter((_, i) => i === 0 || i >= 12)
    .map((s) => cardImageSrc(s).webp);

const suits = SUIT_ORDER.map((suit) => ({
  suit,
  label: SUIT_LABELS[suit],
  ...SUIT_META[suit],
  paper: SUIT_PAPER[suit],
  faces: suitFaces(suit),
}));

useHead({
  title: MASTI_TITLE,
  meta: [
    { name: 'description', content: MASTI_DESCRIPTION },
    {
      name: 'keywords',
      content:
        'масти таро, кубки таро, пентакли таро, мечи таро, жезлы таро, младшие арканы масти, стихии мастей таро',
    },
    { property: 'og:title', content: MASTI_TITLE },
    { property: 'og:description', content: MASTI_DESCRIPTION },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: MASTI_TITLE },
    { name: 'twitter:description', content: MASTI_DESCRIPTION },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: 'https://tarotmeow.ru/karty/masti' },
    { rel: 'alternate', hreflang: 'ru', href: 'https://tarotmeow.ru/karty/masti' },
    { rel: 'alternate', hreflang: 'x-default', href: 'https://tarotmeow.ru/karty/masti' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Карты', item: 'https://tarotmeow.ru/karty' },
          { '@type': 'ListItem', position: 3, name: 'Масти', item: 'https://tarotmeow.ru/karty/masti' },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Масти Таро',
        numberOfItems: suits.length,
        itemListElement: suits.map((s, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: s.label,
          url: `https://tarotmeow.ru/karty/masti/${s.suit}`,
        })),
      }),
    },
  ],
});
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Карты', to: '/karty' }, { label: 'Масти' }]" />

      <!-- Header -->
      <header class="mb-12 text-center md:mb-16">
        <div class="type-overline mb-3.5 text-primary-ink">Младшие арканы</div>
        <h1 class="type-display">Масти Таро</h1>
        <p class="type-body mx-auto mt-3.5 max-w-2xl text-muted-foreground md:text-[17px]">
          56 младших арканов делятся на четыре масти — по числу стихий. Кубки — это вода и чувства,
          Пентакли — земля и материя, Мечи — воздух и мысли, Жезлы — огонь и действие. Выбери масть,
          чтобы прочитать её разбор и все 14 карт.
        </p>
      </header>

      <!-- Suit grid: a patch of each suit's own fabric, its Ace, Queen and King fanned in the corner -->
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <RouterLink
          v-for="(s, i) in suits"
          :key="s.suit"
          :to="`/karty/masti/${s.suit}`"
          class="m-patch paper-sheet relative flex min-h-[210px] flex-col overflow-hidden p-6 text-foreground md:p-7"
          :class="[s.paper.stock, s.paper.fabric]"
        >
          <div class="relative z-[1] flex flex-1 flex-col pr-24 md:pr-32">
            <span
              class="seo-tag paper-tag type-overline w-fit px-2.5 py-0.5"
              :style="{ '--tilt': `${i % 2 ? 1.2 : -1.5}deg` }"
            >
              {{ s.element }} · 14 карт
            </span>
            <h2 class="type-title mt-4">{{ s.label }}</h2>
            <p class="type-body mt-2.5">{{ s.theme }}</p>
            <span class="type-label mt-auto inline-block pt-5">
              Открыть масть →
            </span>
          </div>
          <span
            class="absolute -bottom-5 right-3 z-[1] block"
            aria-hidden="true"
          >
            <CardFan
              :faces="s.faces"
              :card-width="46"
              :spread="11"
            />
          </span>
        </RouterLink>
      </div>

      <!-- CTA -->
      <ScrollReveal class="mt-16 md:mt-24">
        <SeoFinalCta>
          <h2 class="type-title">Не знаешь, какая масть выпала?</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            Собери расклад — кот вытянет карты и объяснит, что значит их масть именно в твоей
            ситуации. Первый расклад бесплатно.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
            <Button
              as-child
              variant="ghost"
              size="lg"
            >
              <RouterLink to="/karty">Все карты</RouterLink>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
.m-patch {
  transition: translate 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}
@media (hover: hover) {
  .m-patch:hover {
    translate: 0 -3px;
  }
}
.m-patch:active {
  translate: 0 1px;
}
.m-patch:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .m-patch {
    transition: none;
  }
}
</style>
